import {
  type AppProposal,
  type CompanyQualificationSheet,
  type ConfigurationProposal,
  type SetupStage,
  type UsageProposal,
  SETUP_STAGES,
} from '@shared/types/company-qualification';
import { USAGE_PLACE_LABELS, USAGE_RHYTHM_LABELS, today } from '@/lib/company-qualification';

/**
 * « Mise en place » (13/10/2026): what the qualification proposes to set up,
 * laid out as a pipeline — connect the tools, the profile, the CRM, the apps,
 * the rituals. Every stage opens at any time and can be skipped; every line is
 * done (dated) or skipped. Objectives are left out: they have their own step.
 */

export const SETUP_STAGE_LABELS: Record<SetupStage, string> = {
  connections: 'Connecter vos outils',
  profile: 'Profil et documents',
  crm: 'Votre CRM',
  apps: 'Applications',
  rituals: 'Rituels',
};

export type SetupList = 'configuration' | 'apps' | 'usage';

export type SetupSource =
  | { list: 'configuration'; entry: ConfigurationProposal }
  | { list: 'apps'; entry: AppProposal }
  | { list: 'usage'; entry: UsageProposal };

/** What a line is: a task done once, an app to build, a reflex for Domino, a ritual for Aujourd'hui. */
export type SetupItemKind = 'task' | 'app' | 'reflex' | 'ritual';

export interface SetupItem {
  id: string;
  stage: SetupStage;
  kind: SetupItemKind;
  title: string;
  detail: string;
  /** Done by hand, by the change Domino applied, or seen in the connected tools. */
  done: boolean;
  detected: boolean;
  skipped: boolean;
  source: SetupSource;
}

export type SetupStageStatus = 'done' | 'skipped' | 'started' | 'todo';

export interface SetupStageView {
  stage: SetupStage;
  items: SetupItem[];
  status: SetupStageStatus;
  /** Lines done, out of the lines not skipped. */
  doneCount: number;
  openCount: number;
}

/** OAuth providers a connection proposal may name; the closed list of what Domino connects. */
const PROVIDERS: Array<{ provider: string; label: string; pattern: RegExp }> = [
  { provider: 'gmail', label: 'Gmail', pattern: /gmail|messagerie/i },
  { provider: 'google_calendar', label: 'Agenda', pattern: /calendar|agenda/i },
  { provider: 'hubspot', label: 'HubSpot', pattern: /hubspot/i },
  { provider: 'google_workspace', label: 'Drive', pattern: /drive|sheets/i },
];

/** The providers a connection proposal names, with whether each is connected. */
export const connectionsOf = (text: string, connected: ReadonlySet<string>) =>
  PROVIDERS.filter(({ pattern }) => pattern.test(text)).map(({ provider, label }) => ({ provider, label, connected: connected.has(provider) }));

const kindOf = (source: SetupSource): SetupItemKind => {
  if (source.list === 'apps') return 'app';
  if (source.list === 'configuration' || source.entry.rhythm === 'once') return 'task';
  return source.entry.kind === 'reflex' ? 'reflex' : 'ritual';
};

/** Where a one-time usage is done. */
const ONCE_STAGES: Partial<Record<UsageProposal['place'], SetupStage>> = { apps: 'apps', forge: 'apps', accounts: 'crm' };

/** Where a proposal is set up; null for the objectives, set in their own step. */
const stageOf = (source: SetupSource): SetupStage | null => {
  if (source.list === 'apps') return 'apps';
  if (source.list === 'usage') {
    const kind = kindOf(source);
    if (kind === 'ritual') return 'rituals';
    if (kind === 'reflex') return 'apps';
    return source.entry.place === 'objectives' ? null : ONCE_STAGES[source.entry.place] ?? 'profile';
  }
  switch (source.entry.area) {
    case 'connections': return 'connections';
    case 'profile': case 'documents': return 'profile';
    case 'crm': return 'crm';
    case 'objectives': return null;
  }
};

const describe = (source: SetupSource): { title: string; detail: string } => {
  switch (source.list) {
    case 'configuration': return { title: source.entry.text, detail: source.entry.why ?? '' };
    case 'apps': return { title: source.entry.title, detail: source.entry.bottleneck };
    case 'usage': return {
      title: source.entry.what,
      detail: `${USAGE_RHYTHM_LABELS[source.entry.rhythm]}, ${USAGE_PLACE_LABELS[source.entry.place]} · ${source.entry.who}`,
    };
  }
};

const sources = (sheet: CompanyQualificationSheet): SetupSource[] => [
  ...sheet.proposal.configuration.map((entry) => ({ list: 'configuration' as const, entry })),
  ...sheet.proposal.apps.map((entry) => ({ list: 'apps' as const, entry })),
  ...sheet.proposal.usage.map((entry) => ({ list: 'usage' as const, entry })),
];

/** Every stage that has something to set up, in the pipeline's order. */
export const setupStages = (sheet: CompanyQualificationSheet, connected: ReadonlySet<string> = new Set()): SetupStageView[] => {
  const items = sources(sheet).flatMap((source): SetupItem[] => {
    const stage = stageOf(source);
    if (!stage) return [];
    const providers = source.list === 'configuration' && source.entry.area === 'connections' ? connectionsOf(source.entry.text, connected) : [];
    const detected = providers.length > 0 && providers.every((entry) => entry.connected);
    return [{
      id: source.entry.id, stage, kind: kindOf(source), ...describe(source), source, detected,
      done: Boolean(source.entry.doneAt) || detected,
      skipped: source.entry.decision === 'dismissed',
    }];
  });
  return SETUP_STAGES.flatMap((stage): SetupStageView[] => {
    const stageItems = items.filter((item) => item.stage === stage);
    if (stageItems.length === 0) return [];
    const open = stageItems.filter((item) => !item.skipped);
    const doneCount = open.filter((item) => item.done).length;
    const status: SetupStageStatus = open.length > 0 && doneCount === open.length ? 'done'
      : sheet.setup.skipped.includes(stage) ? 'skipped'
      : doneCount > 0 ? 'started' : 'todo';
    return [{ stage, items: stageItems, status, doneCount, openCount: open.length }];
  });
};

/** The stage to show first: the first one neither done nor skipped, else the first. */
export const currentStage = (stages: SetupStageView[]): SetupStage | null =>
  (stages.find((view) => view.status === 'started' || view.status === 'todo') ?? stages[0])?.stage ?? null;

/** The one thing to do next: the first open line of the first stage neither done nor skipped. */
export const nextSetupItem = (stages: SetupStageView[]): SetupItem | null => {
  for (const view of stages) {
    if (view.status === 'done' || view.status === 'skipped') continue;
    const item = view.items.find((entry) => !entry.done && !entry.skipped);
    if (item) return item;
  }
  return null;
};

/** Every stage done or skipped. */
export const setupFinished = (stages: SetupStageView[]): boolean =>
  stages.length > 0 && stages.every((view) => view.status === 'done' || view.status === 'skipped');

const updateEntry = (
  sheet: CompanyQualificationSheet, list: SetupList, id: string, patch: Record<string, unknown>,
): CompanyQualificationSheet => ({
  ...sheet,
  proposal: {
    ...sheet.proposal,
    [list]: (sheet.proposal[list] as Array<{ id: string }>).map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)),
  },
});

export const markDone = (sheet: CompanyQualificationSheet, list: SetupList, id: string, done: boolean): CompanyQualificationSheet =>
  updateEntry(sheet, list, id, { doneAt: done ? today() : undefined, ...(done ? { decision: 'accepted' } : {}) });

/** A line skipped is kept, greyed, and can be taken back. */
export const skipItem = (sheet: CompanyQualificationSheet, list: SetupList, id: string, skipped: boolean): CompanyQualificationSheet =>
  updateEntry(sheet, list, id, { decision: skipped ? 'dismissed' : 'proposed', ...(skipped ? { doneAt: undefined } : {}) });

export const skipStage = (sheet: CompanyQualificationSheet, stage: SetupStage, skipped: boolean): CompanyQualificationSheet => ({
  ...sheet,
  setup: {
    skipped: skipped
      ? SETUP_STAGES.filter((entry) => entry === stage || sheet.setup.skipped.includes(entry))
      : sheet.setup.skipped.filter((entry) => entry !== stage),
  },
});

/** A ritual added to (or removed from) Aujourd'hui: the line is in place while it is there. */
export const markRitual = (sheet: CompanyQualificationSheet, usageId: string, ritualId: string | null): CompanyQualificationSheet =>
  updateEntry(sheet, 'usage', usageId, ritualId ? { ritualId, doneAt: today(), decision: 'accepted' } : { ritualId: undefined, doneAt: undefined });

/** What a ritual is called in Aujourd'hui: its first sentence; the rest is its detail. */
export const ritualTexts = (what: string): { title: string; detail: string | null } => {
  const end = what.search(/[.;](\s|$)/);
  const first = (end > 0 ? what.slice(0, end) : what).trim();
  const title = first.length <= 180 ? first : `${first.slice(0, first.lastIndexOf(' ', 177))}…`;
  const rest = what.slice(first.length).replace(/^[.;\s]+/, '').trim();
  return { title, detail: rest ? rest.slice(0, 1500) : null };
};
